import { useEffect } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Stack, Box } from '@mui/material'
import {
  ObservationLogFormData,
  ObservationLogFormActions,
} from '@/components/forms'
import {
  ControlledTextField,
  ControlledDateTimeField,
  ControlledSelect,
  ControlledMediaUpload,
} from '@/components/inputs'
import dayjs from 'dayjs'
import { yupResolver } from '@hookform/resolvers/yup'
import { useApiErrorData } from '@/hooks/forms/useApiErrorData'
import { useAuth } from '@/hooks/useAuth'
import { FormComponentProps, UserGroups } from '@/types'
import {
  BATS_BIRDS_COUNTING_METHOD_OPTIONS,
  BATS_GEDRAG_OPTIONS,
  BATS_SPECIE_OPTIONS,
  UNKNOWN_BAT_SPECIE_OPTION_VALUE,
  OTHER_SPECIE_OPTION_VALUE,
  OTHER_GEDRAG_OPTION_VALUE,
  PLACE_FOUND_OPTION_VALUE,
  PLACE_FOUND_OPTIONS,
  PLACE_TYPE_OPTIONS,
} from '../fieldsOptions'

import { schema } from './validationSchema'

export const BatsForm = ({
  onSubmit,
  defaultValues,
  isLoading,
  apiErrorData,
}: FormComponentProps<ObservationLogFormData>): JSX.Element => {
  const { t } = useTranslation('observationLogs')

  const { hasPermission } = useAuth()

  const methods = useForm<ObservationLogFormData>({
    mode: 'onChange',
    defaultValues: {
      specie: '',
      unknown_specie: '',
      other_specie: '',
      amount: '',
      counting_method: '',
      behaviour: '',
      other_behaviour: '',
      recording_number: '',
      place_type: '',
      first_fledgling_time: '',
      place_type_notes: '',
      notes: '',
      address: '',
      observed_at: '',
      is_place_found: '',
      photos: undefined,
      sound_recordings: undefined,
    },
    values: defaultValues,
    resolver: yupResolver(schema),
  })

  useApiErrorData<ObservationLogFormData>(apiErrorData, methods)

  const watchedSpecie = methods.watch('specie')
  const watchedIsPlaceFound = methods.watch('is_place_found')

  useEffect(() => {
    if (
      methods.getValues('is_place_found') === PLACE_FOUND_OPTION_VALUE &&
      !methods.getValues('first_fledgling_time')
    ) {
      methods.setValue('first_fledgling_time', dayjs())
    }
  }, [watchedIsPlaceFound])

  const disabledForTheUser = !hasPermission([
    UserGroups.MANAGER,
    UserGroups.EMPLOYEE,
    UserGroups.DATA_CONTROLLER,
  ])

  return (
    <FormProvider {...methods}>
      <Stack mt={4} width="100%" alignItems="stretch" spacing={4}>
        <Stack direction="row" spacing={2} justifyContent="flex-start">
          <Box width="35%">
            <ControlledSelect
              control={methods.control}
              name="specie"
              label={t('observation-log.fieldLabels.specie')}
              options={BATS_SPECIE_OPTIONS}
              errorMessagePrefix=""
              fullWidth={true}
            />
          </Box>
          {watchedSpecie === UNKNOWN_BAT_SPECIE_OPTION_VALUE && (
            <Box width="35%">
              <ControlledTextField
                control={methods.control}
                name="unknown_specie"
                placeholder={t('observation-log.fieldLabels.unknownSpecie')}
                errorMessagePrefix=""
              />
            </Box>
          )}
          {watchedSpecie === OTHER_SPECIE_OPTION_VALUE && (
            <Box width="35%">
              <ControlledTextField
                control={methods.control}
                name="other_specie"
                placeholder={t('observation-log.fieldLabels.otherSpecie')}
                errorMessagePrefix=""
              />
            </Box>
          )}
          <Box width="15%">
            <ControlledTextField
              control={methods.control}
              name="amount"
              placeholder={t('observation-log.fieldLabels.amount')}
              type="number"
              disabled={disabledForTheUser}
              errorMessagePrefix=""
            />
          </Box>
          <Box width="15%">
            <ControlledSelect
              control={methods.control}
              name="counting_method"
              label={t('observation-log.fieldLabels.countingMethod')}
              options={BATS_BIRDS_COUNTING_METHOD_OPTIONS}
              disabled={disabledForTheUser}
              errorMessagePrefix=""
            />
          </Box>
          <Box width="15%">
            <ControlledTextField
              control={methods.control}
              name="recording_number"
              placeholder={t('observation-log.fieldLabels.recordingNumber')}
              disabled={disabledForTheUser}
              errorMessagePrefix=""
            />
          </Box>
        </Stack>
        <Stack direction="row" spacing={2} justifyContent="flex-start">
          <Box width="35%">
            <ControlledSelect
              control={methods.control}
              name="behaviour"
              label={t('observation-log.fieldLabels.behaviour')}
              options={BATS_GEDRAG_OPTIONS}
              errorMessagePrefix=""
            />
          </Box>
          {methods.watch('behaviour') === OTHER_GEDRAG_OPTION_VALUE && (
            <Box width="35%">
              <ControlledTextField
                control={methods.control}
                name="other_behaviour"
                placeholder={t('observation-log.fieldLabels.otherBehaviour')}
                errorMessagePrefix=""
              />
            </Box>
          )}
        </Stack>

        <Box width="35%">
          <ControlledSelect
            control={methods.control}
            name="is_place_found"
            label={t('observation-log.fieldLabels.isPlaceFound')}
            options={PLACE_FOUND_OPTIONS}
            disabled={disabledForTheUser}
            errorMessagePrefix=""
          />
        </Box>
        {methods.watch('is_place_found') === PLACE_FOUND_OPTION_VALUE && (
          <>
            <Stack direction="row" spacing={2} justifyContent="flex-start">
              <Box width="35%">
                <ControlledSelect
                  control={methods.control}
                  name="place_type"
                  label={t('observation-log.fieldLabels.placeType')}
                  options={PLACE_TYPE_OPTIONS}
                  disabled={disabledForTheUser}
                  errorMessagePrefix=""
                />
              </Box>
              <ControlledDateTimeField
                name="first_fledgling_time"
                control={methods.control}
                label={t('observation-log.fieldLabels.firstFledglingTime')}
                disabled={disabledForTheUser}
                errorMessagePrefix=""
              />
            </Stack>

            <ControlledTextField
              name="address"
              control={methods.control}
              placeholder={t('observation-log.fieldLabels.address')}
              disabled={disabledForTheUser}
              errorMessagePrefix=""
            />

            <ControlledTextField
              control={methods.control}
              name="place_type_notes"
              placeholder={t('observation-log.fieldLabels.placeTypeNotes')}
              multiline={true}
              rows={4}
              disabled={disabledForTheUser}
              errorMessagePrefix=""
              inputProps={{
                style: {
                  padding: 0,
                },
              }}
            />
          </>
        )}

        <ControlledTextField
          control={methods.control}
          name="notes"
          placeholder={t('observation-log.fieldLabels.notes')}
          multiline={true}
          rows={3}
          inputProps={{
            style: {
              padding: 0,
            },
          }}
          disabled={disabledForTheUser}
          errorMessagePrefix=""
        />

        <ControlledDateTimeField
          name="observed_at"
          control={methods.control}
          label={t('observation-log.fieldLabels.observedAt')}
          disabled={disabledForTheUser}
          errorMessagePrefix=""
        />

        <ControlledMediaUpload
          name="photos"
          control={methods.control}
          label={t('observation-log.fieldLabels.media')}
          disabled={disabledForTheUser}
          errorMessagePrefix=""
        />

        <ControlledMediaUpload
          name="sound_recordings"
          control={methods.control}
          accept="audio/wav,audio/x-wav"
          soundRecord={true}
          label={t('observation-log.fieldLabels.soundRecords', {
            maxNumber: 3,
          })}
          disabled={disabledForTheUser}
          errorMessagePrefix=""
        />

        <ObservationLogFormActions
          onSubmit={onSubmit}
          defaultValues={defaultValues}
          isLoading={isLoading}
          methods={methods}
        />
      </Stack>
    </FormProvider>
  )
}
